import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import Tooltip from 'components/misc/Tooltip'
import { highlightText } from 'lib/highlight-text'
import styles from './styles/Authors.css'
import { Link } from './Link'
import isSlug from '../../lib/is-slug'

const LARGE_BREAKPOINT = 60 * 16

const TRUNCATE_MIN = 3
const TRUNCATE_MAX = 14

export const AuthorList = ({ className, authors, truncateAt, year, truncated }) => {
  authors = authors?.sort(
    (a, b) => a?.authorSequenceNumber - b?.authorSequenceNumber
  )

  const truncatedAuthors = authors?.slice(0, authors.length >= truncateAt ? truncateAt : undefined)

  const [expandAuthors, setExpandAuthors] = useState(false)

  const renderList = expandAuthors ? authors : truncatedAuthors.slice(0, truncated ? TRUNCATE_MIN : undefined)
  const wasTruncated = authors && authors.length > renderList.length

  return (
    <div className={classNames(styles.authors, className)}>
      {renderList.map((author, index) => (
        author?.authorSlug && isSlug(author?.authorSlug)
          ? (
            <div className={styles.author} key={`${index}-${author?.authorSlug}`}>
              <Link blue to={`/authors/${author?.authorSlug}`}><span dangerouslySetInnerHTML={{ __html: author.highlightedName }} /></Link>
              {author?.affiliationSlug
                ? (
                  <Tooltip
                    content={author.affiliation}
                    className={styles.tooltipInner}
                    tooltipClassName={styles.tooltip}
                  >
                    <sup className={styles.affiliationSup}><Link blue to={`/affiliations/${author?.affiliationSlug}`}>{index + 1}</Link></sup>
                  </Tooltip>
                  )
                : <sup className={styles.affiliationSup}>{index + 1}</sup>}
              <span>{index !== renderList.length - 1 ? ', ' : ' '}</span>
            </div>
            )
          : (
            <div className={styles.author} key={`${index}-${author?.name}`}>
              <span dangerouslySetInnerHTML={{ __html: author.highlightedName }} />
              <span>{index !== renderList.length - 1 ? ', ' : ' '}</span>
            </div>
            )
      ))}

      {(!expandAuthors && wasTruncated) && <span key='et-al' onClick={() => setExpandAuthors(true)} className={styles.link}>et al.</span>}
      {year && <span key='year'> {year}</span>}
      {expandAuthors && (<span key='see-fewer'>{' '}<span onClick={() => setExpandAuthors(false)} className={styles.link}>See fewer authors</span></span>)}
    </div>
  )
}

export const Authors = ({ className, year, authors, truncated, textMatch }) => {
  const processedAuthors = authors.map(author => {
    const name = truncated
      ? (
          typeof author === 'string' ? author : `${author.family || author.authorName || ''}`.trim()
        )
      : (
          typeof author === 'string' ? author : `${author.given || author.authorName || ''} ${author.family || ''}`.trim()
        )
    const highlightedName = highlightText(name, textMatch)

    return {
      ...author,
      name,
      highlightedName
    }
  }).filter(
    author => !!author.name
  )
  const [rendered, setRendered] = useState(false)

  useEffect(() => {
    setRendered(true)
  }, [])

  const large = rendered ? window.innerWidth >= LARGE_BREAKPOINT : false
  const truncateAt = large ? TRUNCATE_MAX : TRUNCATE_MIN
  const isTruncated = (truncated && authors.length > TRUNCATE_MIN) || (truncateAt && authors.length >= truncateAt)

  return (isTruncated)
    ? (
      <AuthorList className={className} authors={processedAuthors} truncated={isTruncated} truncateAt={truncateAt} year={year} />
      )
    : (
      <AuthorList className={className} authors={processedAuthors} truncated={isTruncated} year={year} />
      )
}

Authors.propTypes = {
  authors: PropTypes.array,
  year: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]),
  truncated: PropTypes.bool,
  firstNames: PropTypes.bool
}

Authors.defaultProps = {
  truncated: false,
  firstNames: true
}

export default Authors
